import React, { useMemo, useState, useEffect } from "react";
import { Box, Collapse, Stack, TextField, Typography, Paper, Divider, InputAdornment, Chip, Button } from "@mui/material";
import { useCalculateDutiesQuery, DutyCalculationInput, LineType, deriveMetalTypeEntries } from "@/services/product-catalogue";
import { CountrySelect } from "@/pages/COMMON/product-catalogue/components/product-select-fields";
import { useLazyCheckMetalPercentageRequiredQuery } from "@/services/product-catalogue/metal-percentage-api";
import CreateProductForm from "@/pages/COMMON/product-catalogue/components/create-product-form";
import { useProductAnalysisPolling } from "@/hooks/useProductAnalysisPolling";
import { useGetProductsWithTariffsQuery } from "@/services/product-catalogue";
import DatePicker from "@/components/date-picker";
import { useGetDutyColumnsQuery, DutyColumn } from "@/services/product-catalogue/duty-columns-api";
import { Star as StarIcon, ExpandMore as ExpandMoreIcon, CalculateOutlined as CalculateIcon } from "@mui/icons-material";
import { getAuthorityStyle } from "@/pages/COMMON/product-catalogue/components/rule-form-section/helpers";
import { tokens } from "@/tokens";
import { useLinkChatToProductMutation } from "@/services/tariff-engineering";
import { useAppSelector } from "@/store";
import { roles } from "@/roles";

export interface InlineDutyCalculatorState {
  mainExpanded: boolean;
  detailsExpanded: boolean;
  countryOfOrigin: string;
  metalPercentage: string;
  showMetalPercentage: boolean;
  effectiveDate: string;
}

interface InlineDutyCalculatorProps {
  // When provided, the agent's GenUI product-creation flow auto-links
  // the source chat to the new product so tariff-engineering research
  // on that product picks up the chat's history as context.
  chatId?: string;
  htsCode?: string | null;
  defaultOpen?: boolean;
  displayCode?: string; // Pretty code string to display explicitly
  initialState?: Partial<InlineDutyCalculatorState>;
  onStateChange?: (state: InlineDutyCalculatorState) => void;
}

const InlineDutyCalculator: React.FC<InlineDutyCalculatorProps> = ({ htsCode, defaultOpen = true, displayCode, initialState, onStateChange, chatId }) => {
  // Main expansion (Form + Result Summary) - default to true as requested "pre expanded"
  const [mainExpanded, setMainExpanded] = useState(initialState?.mainExpanded ?? defaultOpen);
  // Details expansion (Breakdown) - default to false
  const [detailsExpanded, setDetailsExpanded] = useState(initialState?.detailsExpanded ?? false);

  const [countryOfOrigin, setCountryOfOrigin] = useState(initialState?.countryOfOrigin ?? "");
  const [metalPercentage, setMetalPercentage] = useState<string>(initialState?.metalPercentage ?? "");
  const [showMetalPercentage, setShowMetalPercentage] = useState(initialState?.showMetalPercentage ?? false);
  const [metalBreakdownState, setMetalBreakdownState] = useState<Record<string, string>>({});
  const [checkMetalRequired, { data: metalReqData }] = useLazyCheckMetalPercentageRequiredQuery();
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  // GenUI auto-link: when a product is created from this chat via the
  // CreateProductForm below, attach the source chatId to the new
  // product. Mutation + scope are read here at render time; the actual
  // call fires from onProductCreated.
  const [linkChatToProduct] = useLinkChatToProductMutation();
  const linkUser = useAppSelector((s) => s.auth.user);
  const linkSelectedTenant = useAppSelector(
    (s) => s.tenantManagement.selectedTenant
  );
  const linkSelectedClient = useAppSelector((s) => s.client.selectedClient);
  const autoLinkScope = useMemo(() => {
    const args: { tenantId?: string; clientId?: string } = {};
    const isAdmin = linkUser?.userRole === roles.TDADMIN;
    const isTDAdminTCD =
      isAdmin && linkSelectedTenant?.tenantType === "TRADEDIVE_CLIENT";
    if (linkSelectedTenant?._id) args.tenantId = linkSelectedTenant._id;
    if (linkSelectedClient?._id) {
      args.clientId = linkSelectedClient._id;
    } else if (isTDAdminTCD && linkSelectedTenant?._id) {
      args.clientId = linkSelectedTenant._id;
    }
    return args;
  }, [
    linkUser,
    linkSelectedTenant?._id,
    linkSelectedTenant?.tenantType,
    linkSelectedClient?._id,
  ]);
  const [effectiveDate, setEffectiveDate] = useState<string>(() => {
    if (initialState?.effectiveDate) return initialState.effectiveDate;
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  });

  // Notify parent of state changes for persistence
  useEffect(() => {
    if (onStateChange) {
      onStateChange({
        mainExpanded,
        detailsExpanded,
        countryOfOrigin,
        metalPercentage,
        showMetalPercentage,
        effectiveDate
      });
    }
  }, [mainExpanded, detailsExpanded, countryOfOrigin, metalPercentage, showMetalPercentage, effectiveDate, onStateChange]);

  // Lightweight hook setup to trigger risk analysis polling after creation
  const { refetch } = useGetProductsWithTariffsQuery({ effectiveDate }, { skip: true });
  const { addProductPolling } = useProductAnalysisPolling({ queryParams: { effectiveDate }, enabled: false } as any);

  const isTenDigit = useMemo(() => Boolean(htsCode && /^\d{10}$/.test(htsCode.replace(/\D/g, ""))), [htsCode]);

  useEffect(() => {
    if (!isTenDigit) setMainExpanded(false);
  }, [isTenDigit]);

  useEffect(() => {
    if (isTenDigit && htsCode && countryOfOrigin) {
      checkMetalRequired({ htsCode, countryOfOrigin });
    } else {
      setShowMetalPercentage(false);
    }
  }, [isTenDigit, htsCode, countryOfOrigin, checkMetalRequired]);

  // Detect multi-metal mode
  const applicableMetalRules = (metalReqData as any)?.applicableMetalRules || [];
  const isMultiMetal = applicableMetalRules.length > 1;

  const metalTypeEntries = useMemo(() => deriveMetalTypeEntries(applicableMetalRules), [applicableMetalRules]);

  useEffect(() => {
    const required = Boolean((metalReqData as any)?.isMetalPercentageRequired);
    setShowMetalPercentage(required);
    if (required && !isMultiMetal && !metalPercentage) {
      setMetalPercentage("100");
    }
    if (required && isMultiMetal) {
      setMetalBreakdownState((prev) => {
        const next = { ...prev };
        metalTypeEntries.forEach(({ key }: { key: string }) => {
          if (!next[key]) next[key] = "50";
        });
        return next;
      });
    }
  }, [metalReqData]);

  const input: DutyCalculationInput | null = useMemo(() => {
    if (!isTenDigit || !htsCode || !countryOfOrigin) return null;

    // Multi-metal mode
    if (isMultiMetal && Object.keys(metalBreakdownState).length > 0) {
      const breakdown: Record<string, number> = {};
      Object.entries(metalBreakdownState).forEach(([key, val]) => {
        const n = Number(val);
        if (!isNaN(n) && n > 0) breakdown[key] = n;
      });
      if (Object.keys(breakdown).length > 0) {
        return {
          htsCode,
          countryOfOrigin,
          effectiveDate,
          metalBreakdown: breakdown as any,
        };
      }
    }

    return {
      htsCode,
      countryOfOrigin,
      effectiveDate,
      metalPercentage: metalPercentage ? Number(metalPercentage) : undefined,
    };
  }, [isTenDigit, htsCode, countryOfOrigin, metalPercentage, effectiveDate, isMultiMetal, metalBreakdownState]);

  const { data, isFetching } = useCalculateDutiesQuery(input!, { skip: !input });
  const dutyColumnsResp = useGetDutyColumnsQuery({ pageNo: 1, pageSize: 500 } as any, { skip: !input });
  const dutyColumns: DutyColumn[] = dutyColumnsResp.data?.data || [];
  const columnMap = useMemo(() => {
    const m = new Map<string, DutyColumn>();
    dutyColumns.forEach((dc) => dc && (dc as any)?._id && m.set((dc as any)._id, dc));
    return m;
  }, [dutyColumns]);

  const footnoteColumns = useMemo(() => {
    const used = new Set<string>();
    ((data as any)?.breakdownLines || []).forEach((line: any) => {
      (line.calculationBreakdown || []).forEach((calc: any) => {
        if (
          calc?.applied &&
          typeof calc?.columnId === "string" &&
          calc.columnId !== "general-duty" &&
          calc.columnId !== "secondary-duty"
        ) {
          used.add(calc.columnId as string);
        }
      });
    });
    return Array.from(used)
      .map((id) => columnMap.get(id))
      .filter((dc): dc is DutyColumn => !!dc && Array.isArray((dc as any).citations) && (dc as any).citations.length > 0);
  }, [data, columnMap]);

  if (!isTenDigit) return null;

  return (
    <Paper 
      className="inline-duty-calculator-root"
      elevation={0} 
      sx={{ 
        border: "1px solid #EBEBEB", 
        borderRadius: tokens.radius.md,
        backgroundColor: "#F7F8F8", 
        overflow: 'hidden',
        position: 'relative'
      }}
    >
      {/* Header */}
      <Box
        onClick={() => setMainExpanded((x) => !x)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
          cursor: "pointer",
          '&:hover': { backgroundColor: 'rgba(0,0,0,0.02)' },
          transition: 'background-color 0.2s ease'
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{ 
                width: 20, 
                height: 20, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                color: '#000' 
            }}>
               <CalculateIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography variant="body2" fontSize={tokens.fontSize.lg} fontWeight={tokens.fontWeight.semibold} color="#030712" fontFamily="Inter Display, sans-serif">
            Duty Calculator
            </Typography>
        </Stack>
        
        <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="body2" fontSize={tokens.fontSize.base} fontWeight={tokens.fontWeight.semibold} color="#687071" fontFamily="Inter Display, sans-serif">
            {mainExpanded ? "Hide" : "Show"}
            </Typography>
            <ExpandMoreIcon sx={{ fontSize: tokens.fontSize.xl, color: '#687071', transform: mainExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
        </Stack>
      </Box>

      {/* Main Expanded Content */}
      <Collapse in={mainExpanded}>
        <Box sx={{ px: 2, pb: 2 }}>
           {/* White Inputs Box */}
           <Paper elevation={0} sx={{ 
               p: 1.5, 
               backgroundColor: tokens.bg.surface,
               borderRadius: tokens.radius.sm,
               boxShadow: '0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px -1px rgba(16, 24, 40, 0.1)'
           }}>
              <Stack direction="row" spacing={3} alignItems="flex-end" flexWrap="wrap" useFlexGap sx={{ rowGap: 2 }}>
                  {/* HTS Code Column */}
                  <Box>
                      <Typography variant="body2" fontSize={tokens.fontSize.lg} color="#AEB3B3" fontFamily="Inter Display, sans-serif" sx={{ mb: 1 }}>
                          HTS Used :
                      </Typography>
                      <Typography variant="body1" fontSize={tokens.fontSize.xl} fontWeight={tokens.fontWeight.heavy} color="#33404D" fontFamily="Inter Display, sans-serif" sx={{ height: 40, display: 'flex', alignItems: 'center' }}>
                          {displayCode || htsCode}
                      </Typography>
                  </Box>

                  {/* Country Column */}
                  <Box sx={{ width: 230 }}>
                      <Typography variant="body2" fontSize={tokens.fontSize.lg} color="#AEB3B3" fontFamily="Inter Display, sans-serif" sx={{ mb: 1 }}>
                          Country of Origin
                      </Typography>
                      <Box sx={{ '& .MuiOutlinedInput-root': { height: 40, paddingRight: '30px !important' }, '& .MuiAutocomplete-input': { padding: '0px 4px !important' } }}>
                        <CountrySelect
                            label=""
                            helperText=""
                            value={countryOfOrigin}
                            onChange={(val: string) => setCountryOfOrigin(val)}
                            countries={[]}
                        />
                      </Box>
                  </Box>

                  {/* Date Column */}
                  <Box sx={{ width: 230 }}>
                      <Typography variant="body2" fontSize={tokens.fontSize.lg} color="#AEB3B3" fontFamily="Inter Display, sans-serif" sx={{ mb: 1 }}>
                          Date
                      </Typography>
                      <Box sx={{ height: 40, '& .MuiOutlinedInput-root': { height: 40 } }}>
                        <DatePicker value={effectiveDate} onChangeValue={(d) => setEffectiveDate(d ?? effectiveDate)} />
                      </Box>
                  </Box>

                  {/* Metal % Column — single or per-metal */}
                  {showMetalPercentage && !isMultiMetal && (
                      <Box sx={{ width: 120 }}>
                        <Typography variant="body2" fontSize={tokens.fontSize.lg} color="#AEB3B3" fontFamily="Inter Display, sans-serif" sx={{ mb: 1 }}>
                            Metal %
                        </Typography>
                        <TextField
                            value={metalPercentage}
                            onChange={(e) => setMetalPercentage(e.target.value)}
                            placeholder="100"
                            size="small"
                            fullWidth
                            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                            sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'white', borderRadius: tokens.radius.md, height: 40 } }}
                        />
                        {metalPercentage && Number(metalPercentage) < 100 && (
                          <Typography variant="caption" color="#AEB3B3" fontFamily="Inter Display, sans-serif" sx={{ mt: 0.5, display: 'block' }}>
                            Non-Metal: {(100 - Number(metalPercentage)).toFixed(1)}%
                          </Typography>
                        )}
                      </Box>
                  )}
                  {showMetalPercentage && isMultiMetal && (
                    <>
                      {metalTypeEntries.map(({ key, label }: { key: string; label: string }) => (
                        <Box key={key} sx={{ width: 120 }}>
                          <Typography variant="body2" fontSize={tokens.fontSize.lg} color="#AEB3B3" fontFamily="Inter Display, sans-serif" sx={{ mb: 1 }}>
                              {label}
                          </Typography>
                          <TextField
                              value={metalBreakdownState[key] || ""}
                              onChange={(e) => setMetalBreakdownState((prev) => ({ ...prev, [key]: e.target.value }))}
                              placeholder="50"
                              size="small"
                              fullWidth
                              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                              sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'white', borderRadius: tokens.radius.md, height: 40 } }}
                          />
                        </Box>
                      ))}
                      <Box sx={{ width: 120, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                        <Typography variant="body2" fontSize={tokens.fontSize.lg} color="#AEB3B3" fontFamily="Inter Display, sans-serif" sx={{ mb: 1 }}>
                            Non-Metal
                        </Typography>
                        <Box sx={{ height: 40, display: 'flex', alignItems: 'center', px: 1, bgcolor: '#F3F4F6', borderRadius: tokens.radius.md, border: '1px solid #E5E7EB' }}>
                          <Typography variant="body2" fontWeight={tokens.fontWeight.bold} color="#33404D" fontFamily="Inter Display, sans-serif">
                            {Math.max(0, 100 - Object.values(metalBreakdownState).reduce((sum, v) => sum + (Number(v) || 0), 0)).toFixed(1)}%
                          </Typography>
                        </Box>
                        {Object.values(metalBreakdownState).reduce((sum, v) => sum + (Number(v) || 0), 0) > 100 && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.25 }}>
                            Exceeds 100%
                          </Typography>
                        )}
                      </Box>
                    </>
                  )}

                  {/* Button Column */}
                  <Box>
                      <Button
                        variant="contained"
                        onClick={() => setIsCreateOpen(true)}
                        disabled={!htsCode || !countryOfOrigin}
                        sx={{ 
                            textTransform: 'none', 
                            background: `linear-gradient(180deg, rgba(255, 255, 255, 0.16) 0%, rgba(255, 255, 255, 0) 100%), ${tokens.brand.primary}`,
                            border: `1px solid ${tokens.bg.surface}`,
                            boxShadow: "inset 0px 4px 46px rgba(255, 255, 255, 0.16)",
                            borderRadius: tokens.radius.md,
                            fontFamily: 'Inter Display, sans-serif',
                            fontWeight: tokens.fontWeight.semibold,
                            height: 36,
                            px: 2,
                            whiteSpace: 'nowrap',
                            mb: '2px',
                            color: (!htsCode || !countryOfOrigin) ? undefined : tokens.bg.surface,
                            '&:hover': {
                              background: tokens.brand.primaryHover,
                              boxShadow: "inset 0px 4px 46px rgba(255, 255, 255, 0.16)",
                            },
                            '&.Mui-disabled': {
                                color: 'rgba(0, 0, 0, 0.26)',
                                boxShadow: 'none',
                                background: 'rgba(0, 0, 0, 0.12)',
                                border: '1px solid transparent',
                            }
                        }}
                      >
                        Create Product
                      </Button>
                  </Box>
              </Stack>
           </Paper>
           
           {/* Result Summary & Details Toggle */}
           {(data || isFetching) && (
              <Box sx={{ mt: 3 }}>
                 {/* Result Row */}
                 <Stack direction="row" alignItems="center" justifyContent="space-between">
                     <Stack direction="row" alignItems="center" spacing={1}>
                        {/* Icon could go here if needed, e.g. calculate icon */}
                        <Typography variant="body2" fontSize={tokens.fontSize.lg} color={tokens.text.body} fontFamily="Inter Display, sans-serif" fontWeight={tokens.fontWeight.semibold}>
                             Total Duty Rate:
                        </Typography>
                        <Typography variant="body1" fontSize={tokens.fontSize.lg} color={tokens.text.body} fontFamily="Inter Display, sans-serif" fontWeight={tokens.fontWeight.heavy}>
                             {isFetching ? "..." : `${((data?.totalDutyRate ?? 0) * 100).toFixed(2)}%`}
                        </Typography>
                     </Stack>

                     {/* Details Toggle Button */}
                     <Button 
                        onClick={() => setDetailsExpanded(prev => !prev)}
                        endIcon={<ExpandMoreIcon sx={{ transform: detailsExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />}
                        sx={{
                            textTransform: 'none',
                            color: '#687071',
                            fontWeight: tokens.fontWeight.semibold,
                            fontSize: tokens.fontSize.base,
                            fontFamily: 'Inter Display, sans-serif',
                            '&:hover': { bgcolor: 'transparent', color: '#33404D' }
                        }}
                     >
                        {detailsExpanded ? "Hide Details" : "View Details"}
                     </Button>
                 </Stack>

                 {/* Detailed Breakdown */}
                 <Collapse in={detailsExpanded}>
                    <Box sx={{ mt: 2 }}>
                        {((data as any)?.secondaryDutyRateString || '').toString().trim().toLowerCase() !== 'null' &&
                            (data as any)?.secondaryDutyRateString && (
                            <Box sx={{ mt: 1, p: 1, bgcolor: tokens.bg.muted, borderRadius: tokens.radius.sm }}>
                            <Typography variant="caption" color="text.secondary" fontFamily="Inter, sans-serif">
                                Specific Duty: <span style={{ fontWeight: tokens.fontWeight.bold, color: tokens.text.body }}>{(data as any).secondaryDutyRateString}</span>
                            </Typography>
                            </Box>
                        )}
                        
                        {(data as any)?.ftaApplicable && (
                            <Box sx={{ mt: 2 }}>
                            <Chip 
                                color="success" 
                                label={`Preferential Origin: ${(data as any)?.ftaProgramName || 'Applicable'} (Duty Exempt)`} 
                                size="small" 
                                sx={{ borderRadius: '4px', fontWeight: tokens.fontWeight.semibold, fontFamily: 'Inter, sans-serif' }}
                            />
                            </Box>
                        )}

                        {!!data && (Array.isArray((data as any).breakdownLines) && (data as any).breakdownLines.length > 0 ? (
                            <Stack spacing={1.5} sx={{ mt: 3 }}>
                            {(data as any).breakdownLines.map((line: any, idx: number) => (
                                <Box key={idx} sx={{ border: `1px solid ${tokens.border.light}`, borderRadius: tokens.radius.md, p: 1.5, bgcolor: '#FAFAFA' }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                    <Chip
                                        label={
                                          line.lineType === LineType.STEEL ? 'Steel (232)' :
                                          line.lineType === LineType.ALUMINUM ? 'Aluminum (232)' :
                                          line.lineType === LineType.COPPER ? 'Copper (232)' :
                                          line.lineType === LineType.METAL ? 'Metal' :
                                          line.lineType === LineType.NONMETAL ? 'Non-Metal' : 'Full Value'
                                        }
                                        size="small"
                                        sx={{
                                        backgroundColor:
                                          line.lineType === LineType.STEEL ? '#FEF3C7' :
                                          line.lineType === LineType.ALUMINUM ? '#E0E7FF' :
                                          line.lineType === LineType.COPPER ? '#FFE4E6' :
                                          line.lineType === LineType.METAL ? '#FFF7ED' :
                                          line.lineType === LineType.NONMETAL ? '#EFF6FF' : tokens.status.successBg,
                                        color:
                                          line.lineType === LineType.STEEL ? '#92400E' :
                                          line.lineType === LineType.ALUMINUM ? '#3730A3' :
                                          line.lineType === LineType.COPPER ? '#9F1239' :
                                          line.lineType === LineType.METAL ? '#C2410C' :
                                          line.lineType === LineType.NONMETAL ? tokens.chip.blue.text : tokens.chip.green.text,
                                        fontWeight: tokens.fontWeight.bold,
                                        borderRadius: '4px',
                                        border: '1px solid transparent',
                                        borderColor:
                                          line.lineType === LineType.STEEL ? '#FDE68A' :
                                          line.lineType === LineType.ALUMINUM ? '#C7D2FE' :
                                          line.lineType === LineType.COPPER ? '#FECDD3' :
                                          line.lineType === LineType.METAL ? '#FFEDD5' :
                                          line.lineType === LineType.NONMETAL ? tokens.status.infoBg : tokens.chip.green.bg,
                                        }}
                                    />
                                    {line.portion < 1 && (
                                        <Typography variant="caption" color="text.secondary" fontFamily="Inter, sans-serif">({(line.portion * 100).toFixed(0)}% of value)</Typography>
                                    )}
                                    </Stack>
                                    <Typography variant="body2" fontWeight={tokens.fontWeight.bold} fontFamily="Inter, sans-serif">{(line.totalRate * 100).toFixed(2)}%</Typography>
                                </Stack>
                                <Divider sx={{ my: 1, borderColor: tokens.border.default }} />
                                {line.applicableRules?.length ? (
                                    <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight={tokens.fontWeight.semibold} sx={{ mb: 0.5, display: 'block' }}>APPLIED RULES</Typography>
                                    <Box component="ul" sx={{ pl: 2.5, my: 0 }}>
                                        {line.applicableRules.map((rule: any, ridx: number) => {
                                        const excluded = (line.exclusionsApplied || []).some((e: any) => e.columnId === rule.columnId);
                                        const color = excluded
                                            ? getAuthorityStyle((line.exclusionsApplied || []).find((e: any) => e.columnId === rule.columnId)?.authority).color
                                            : undefined;
                                        if (rule.columnId === 'secondary-duty') {
                                            const s = ((data as any)?.secondaryDutyRateString || '').toString().trim();
                                            if (!s || s.toLowerCase() === 'null') {
                                            return null;
                                            }
                                            return (
                                            <li key={ridx}>
                                                <Typography variant="body2" fontSize={tokens.fontSize.md} fontFamily="Inter, sans-serif">Specific Duty: {s}</Typography>
                                            </li>
                                            );
                                        }
                                        return (
                                        <li key={ridx}>
                                            <Typography variant="body2" fontSize={tokens.fontSize.md} fontFamily="Inter, sans-serif">
                                            {rule.columnName}: {(rule.rate * 100).toFixed(2)}%
                                            {excluded && (
                                                <>
                                                <StarIcon sx={{ fontSize: tokens.fontSize.lg, color, verticalAlign: 'middle', ml: 0.5 }} />
                                                <span style={{ marginLeft: 4, color: tokens.status.success, fontWeight: tokens.fontWeight.semibold }}>{"→ 0%"}</span>
                                                </>
                                            )}
                                            </Typography>
                                        </li>
                                        );
                                        })}
                                    </Box>
                                    </Box>
                                ) : (
                                    <Typography variant="caption" color="text.secondary" fontStyle="italic">No applicable duties on this portion.</Typography>
                                )}
                                </Box>
                            ))}
                            </Stack>
                        ) : null)}

                        {/* Citations */}
                        {footnoteColumns.length > 0 && (
                            <Paper elevation={0} sx={{ mt: 2, p: 1.5, border: `1px solid ${tokens.border.default}`, borderRadius: tokens.radius.md, bgcolor: tokens.bg.hover }}>
                            <Typography variant="subtitle2" fontSize={tokens.fontSize.base} fontWeight={tokens.fontWeight.heavy} sx={{ mb: 1, textTransform: 'uppercase', color: tokens.text.excerpt }}>Sources</Typography>
                            <Stack spacing={1.5}>
                                {footnoteColumns.map((dc: any, idx: number) => (
                                <Box key={dc._id}>
                                    <Typography variant="body2" fontWeight={tokens.fontWeight.bold} fontSize={tokens.fontSize.md} color={tokens.text.primary}>
                                    {idx + 1}. {dc.name}
                                    </Typography>
                                    <Box component="ul" sx={{ pl: 2.5, my: 0.5 }}>
                                    {(dc.citations || []).map((c: any, cidx: number) => (
                                        <li key={cidx}>
                                            <Typography variant="body2" fontSize={tokens.fontSize.md} sx={{ wordBreak: 'break-all' }}>
                                            <a href={c.url} target="_blank" rel="noopener noreferrer" style={{ color: tokens.brand.primary, textDecoration: 'none' }}>{c.url}</a>
                                            </Typography>
                                        </li>
                                    ))}
                                    </Box>
                                </Box>
                                ))}
                            </Stack>
                            </Paper>
                        )}
                    </Box>
                 </Collapse>
              </Box>
           )}
        </Box>
      </Collapse>

      <CreateProductForm
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onProductCreated={(created) => {
          setIsCreateOpen(false);
          try {
            if (created && addProductPolling) {
              addProductPolling(created, 'creation');
            }
          } catch {}
          try {
            refetch && refetch();
          } catch {}
          // GenUI auto-link: when a product is created from a chat,
          // attach the source chat to the new product so subsequent
          // tariff-engineering research on that product picks up the
          // chat history as context. Idempotent and scope-guarded
          // server-side; failure here is non-fatal (logged only) since
          // the product itself was created successfully.
          try {
            if (created && (created as any)._id && chatId) {
              linkChatToProduct({
                productId: String((created as any)._id),
                chatId,
                ...(autoLinkScope.tenantId
                  ? { tenantId: autoLinkScope.tenantId }
                  : {}),
                ...(autoLinkScope.clientId
                  ? { clientId: autoLinkScope.clientId }
                  : {}),
              }).catch((err) => {
                // eslint-disable-next-line no-console
                console.warn(
                  "[InlineDutyCalculator] auto-link chat→product failed:",
                  err
                );
              });
            }
          } catch {}
        }}
        initialData={{
          htsCode: (displayCode ?? htsCode ?? undefined) as string | undefined,
          countryOfOrigin,
          metalPercentage: showMetalPercentage && metalPercentage ? Number(metalPercentage) : undefined,
        }}
      />
    </Paper>
  );
};

export default InlineDutyCalculator;